/**
 * globalTts.ts — единый владелец озвучки.
 *
 * ГЛАВНОЕ: воспроизведение идёт через Web Audio API (AudioContext +
 * AudioBufferSourceNode), а НЕ через HTMLAudioElement.play().
 *
 * Почему: в разговоре звук играет через несколько секунд после клика
 * (запись микрофона → распознавание → ответ ИИ). К этому моменту "окно
 * пользовательского жеста" в браузере (особенно Яндекс/Chromium-mobile)
 * закрывается, и <audio>.play() молча блокируется автоплей-политикой.
 * AudioContext, однажды резюмированный на жесте (resume() в unlockAudio),
 * остаётся в состоянии "running" и позволяет проигрывать звук в любой
 * момент позже БЕЗ нового жеста. Это и есть корректное решение для
 * отложенной озвучки на мобильных браузерах.
 *
 * Диагностика подтвердила (audiotest.html в Яндекс.Браузере): Web Audio
 * (осциллятор) играет, ctx.state=running. Значит decode+source-node путь
 * рабочий. HTMLAudioElement оставлен только как запасной путь, а нативный
 * speechSynthesis — как самая последняя страховка.
 *
 * Публичный API: unlockAudio(), cancelAll(), enqueue(text, opts?), isPlaying(),
 * setConversationActive(v), isConversationActive().
 */
import { useSettingsStore } from "../store/settings";

export type SpeakOpts = { voice?: string; speed?: number; role?: string };

const TTS_PATH = "/v1/audio/speech";
const DEFAULT_VOICE = "dmitry";
const DEFAULT_SPEED = 1.15;

const synth: SpeechSynthesis | null =
  typeof window !== "undefined" && "speechSynthesis" in window ? window.speechSynthesis : null;

// ───────────────────────── Web Audio (основной путь) ─────────────────────────

type AudioCtxCtor = typeof AudioContext;
function _getCtxCtor(): AudioCtxCtor | null {
  if (typeof window === "undefined") return null;
  return (window.AudioContext || (window as unknown as { webkitAudioContext?: AudioCtxCtor }).webkitAudioContext) ?? null;
}

let _ctx: AudioContext | null = null;
let _currentSource: AudioBufferSourceNode | null = null;

function getCtx(): AudioContext | null {
  if (_ctx) return _ctx;
  const Ctor = _getCtxCtor();
  if (!Ctor) return null;
  try { _ctx = new Ctor(); } catch { _ctx = null; }
  return _ctx;
}

// Резюмируем контекст (можно вызывать вне жеста — если он уже был
// активирован жестом хоть раз, resume() работает без нового жеста).
function resumeCtx(): void {
  const ctx = getCtx();
  if (ctx && ctx.state === "suspended") {
    try { void ctx.resume(); } catch { /* ignore */ }
  }
}

function stopCurrentSource(): void {
  if (_currentSource) {
    try { _currentSource.onended = null; } catch { /* ignore */ }
    try { _currentSource.stop(); } catch { /* ignore */ }
    try { _currentSource.disconnect(); } catch { /* ignore */ }
    _currentSource = null;
  }
}

// decodeAudioData с поддержкой как промис-формы, так и старой колбэк-формы.
function decodeAudio(ctx: AudioContext, buf: ArrayBuffer): Promise<AudioBuffer> {
  return new Promise<AudioBuffer>((resolve, reject) => {
    let settled = false;
    const ok = (b: AudioBuffer) => { if (!settled) { settled = true; resolve(b); } };
    const bad = (e: unknown) => { if (!settled) { settled = true; reject(e instanceof Error ? e : new Error("decode failed")); } };
    try {
      // decodeAudioData может "съесть" (detach) переданный буфер — даём ему копию,
      // чтобы фолбэк на HTMLAudio при необходимости получил целые данные.
      const p = ctx.decodeAudioData(buf.slice(0), ok, bad);
      // Современные браузеры возвращают Promise.
      if (p && typeof (p as Promise<AudioBuffer>).then === "function") {
        (p as Promise<AudioBuffer>).then(ok).catch(bad);
      }
    } catch (e) { bad(e); }
  });
}

function playViaWebAudio(buf: ArrayBuffer, myGen: number): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const ctx = getCtx();
    if (!ctx) { reject(new Error("no AudioContext")); return; }
    if (_gen !== myGen) { resolve(); return; }
    // На всякий случай поднимаем контекст перед проигрыванием.
    const ensureRunning = ctx.state === "suspended" ? ctx.resume().catch(() => {}) : Promise.resolve();
    Promise.resolve(ensureRunning).then(() => decodeAudio(ctx, buf)).then((audioBuf) => {
      if (_gen !== myGen) { resolve(); return; }
      stopCurrentSource();
      const src = ctx.createBufferSource();
      src.buffer = audioBuf;
      src.connect(ctx.destination);
      _currentSource = src;
      src.onended = () => {
        if (_currentSource === src) _currentSource = null;
        try { src.disconnect(); } catch { /* ignore */ }
        resolve();
      };
      try { src.start(0); } catch (e) { reject(e instanceof Error ? e : new Error("source start failed")); }
    }).catch((e) => reject(e instanceof Error ? e : new Error("web audio play failed")));
  });
}

// ───────────────── HTMLAudioElement (запасной путь воспроизведения) ─────────────────

let _audio: HTMLAudioElement | null = null;
function getAudio(): HTMLAudioElement {
  if (!_audio) {
    _audio = new Audio();
    _audio.preload = "auto";
    _audio.setAttribute("playsinline", "true");
    _audio.setAttribute("webkit-playsinline", "true");
    _audio.style.display = "none";
    try { if (typeof document !== "undefined" && document.body) document.body.appendChild(_audio); } catch { /* ignore */ }
  }
  return _audio;
}

let _queue: { text: string; opts?: SpeakOpts }[] = [];
let _abort = new AbortController();
let _draining = false;
let _gen = 0;

let _conversationActive = false;
export function setConversationActive(v: boolean) { _conversationActive = v; }
export function isConversationActive(): boolean { return _conversationActive; }

// Разблокировка медиа на жесте. Главное — резюмировать AudioContext именно
// здесь (внутри пользовательского клика), чтобы он остался "running" и мог
// проигрывать отложенную озвучку без нового жеста. Дополнительно проигрываем
// крошечный беззвучный буфер — стандартный анлок-приём для iOS/Safari.
let _unlocked = false;
export function unlockAudio() {
  try {
    const ctx = getCtx();
    if (ctx) {
      if (ctx.state === "suspended") { try { void ctx.resume(); } catch { /* ignore */ } }
      // Беззвучный буфер «прогревает» контекст на жесте.
      try {
        const b = ctx.createBuffer(1, 1, 22050);
        const s = ctx.createBufferSource();
        s.buffer = b;
        s.connect(ctx.destination);
        s.start(0);
      } catch { /* ignore */ }
    }
  } catch { /* ignore */ }
  // На всякий случай прогреваем и HTMLAudio-элемент (для фолбэка).
  if (!_unlocked) {
    try {
      const a = getAudio();
      a.muted = true;
      const p = a.play();
      if (p && typeof p.then === "function") {
        p.then(() => { try { a.pause(); } catch { /* ignore */ } try { a.currentTime = 0; } catch { /* ignore */ } a.muted = false; _unlocked = true; })
         .catch(() => { a.muted = false; });
      } else { a.muted = false; _unlocked = true; }
    } catch { /* ignore */ }
  }
}

export function cancelAll() {
  _gen++;
  _queue = [];
  try { _abort.abort(); } catch { /* ignore */ }
  _abort = new AbortController();
  stopCurrentSource();
  if (_audio) { try { _audio.pause(); _audio.src = ""; } catch { /* ignore */ } }
  if (synth) { try { synth.cancel(); } catch { /* ignore */ } }
}

export function isPlaying(): boolean {
  return _draining || _queue.length > 0 || !!_currentSource || (!!_audio && !_audio.paused) || (!!synth && synth.speaking);
}

export function enqueue(text: string, opts?: SpeakOpts) {
  const t = (text || "").trim();
  if (!t) return;
  _queue.push({ text: t, opts });
  void drain();
}

async function drain() {
  if (_draining) return;
  _draining = true;
  const myGen = _gen;
  resumeCtx();
  while (_gen === myGen && _queue.length > 0) {
    const job = _queue.shift()!;
    try {
      const buf = await fetchSpeech(job.text, job.opts, _abort.signal);
      if (_gen !== myGen) break;
      await playArrayBuffer(buf, myGen);
    } catch (err) {
      if (_gen !== myGen) break;
      // eslint-disable-next-line no-console
      console.warn('[globalTts] server TTS failed, using native fallback:', err);
      try { await speakNative(job.text, job.opts, myGen); } catch { /* ignore */ }
    }
  }
  if (_gen === myGen) _draining = false;
}

async function fetchSpeech(text: string, opts: SpeakOpts | undefined, signal: AbortSignal): Promise<ArrayBuffer> {
  const r = await fetch(TTS_PATH, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({
      model: "tts-1",
      input: text,
      voice: opts?.voice ?? DEFAULT_VOICE,
      speed: opts?.speed ?? DEFAULT_SPEED,
      role: opts?.role ?? (useSettingsStore.getState().displayMode === "chat" ? "good" : "neutral"),
    }),
    signal,
  });
  if (!r.ok) throw new Error(`tts http ${r.status}`);
  return r.arrayBuffer();
}

function _playViaElement(buf: ArrayBuffer, myGen: number): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const a = getAudio();
    const url = URL.createObjectURL(new Blob([buf], { type: "audio/mpeg" }));
    const cleanup = () => { try { URL.revokeObjectURL(url); } catch { /* ignore */ } };
    a.onended = () => { cleanup(); resolve(); };
    a.onerror = () => { cleanup(); reject(new Error("audio element error")); };
    if (_gen !== myGen) { cleanup(); resolve(); return; }
    try { a.pause(); } catch { /* ignore */ }
    a.muted = false;
    a.src = url;
    try { a.currentTime = 0; } catch { /* ignore */ }
    const p = a.play();
    if (p && typeof p.then === "function") {
      p.catch(() => { cleanup(); reject(new Error("audio play blocked")); });
    }
  });
}

async function playArrayBuffer(buf: ArrayBuffer, myGen: number): Promise<void> {
  if (buf.byteLength < 100) return;
  // 1) Основной путь — Web Audio (играет отложенно без жеста, см. шапку файла).
  try {
    await playViaWebAudio(buf, myGen);
    return;
  } catch (e) {
    if (_gen !== myGen) return;
    // 2) Запасной путь — HTMLAudioElement (на случай, если Web Audio не смог
    //    декодировать формат на конкретном движке).
    // eslint-disable-next-line no-console
    console.warn('[globalTts] Web Audio failed, falling back to <audio>:', e);
    await _playViaElement(buf, myGen);
  }
}

function speakNative(text: string, opts: SpeakOpts | undefined, myGen: number): Promise<void> {
  if (!synth) return Promise.resolve();
  return new Promise<void>((resolve) => {
    const u = new SpeechSynthesisUtterance(text);
    u.lang = "ru-RU";
    const v = synth.getVoices().find((x) => /ru/i.test(x.lang));
    if (v) u.voice = v;
    u.rate = Math.min(2, Math.max(0.5, opts?.speed ?? DEFAULT_SPEED));
    u.onend = () => resolve();
    u.onerror = () => resolve();
    if (_gen !== myGen) { resolve(); return; }
    try { synth.speak(u); } catch { resolve(); }
  });
}
